/**
 * The interpolated view of the character: a blend between the previous and
 * the newest simulation tick, plus render-only continuous state (facing
 * blend, landing spring, acceleration lean). Updated in place every frame.
 */

import { type CompiledTuning, capsuleHeight, comHeight, S, type SimState, shoulderHeight } from "@lache/sim";
import { halfLifeFactor, type RenderFeel } from "./feel";

const TWO_PI = 2 * Math.PI;

/** The difference from `a` to `b` taken the short way round the circle, in (-pi, pi]. */
function shortWay(a: number, b: number): number {
  const d = b - a;
  return d - TWO_PI * Math.round(d / TWO_PI);
}

/** Time after a catch over which the drawn body axis is pinned onto the pendulum line, s. */
const ATTACH_PIN_SECONDS = 0.2;

export class ViewState {
  x = 0;
  y = 0;
  vx = 0;
  vy = 0;
  ax = 0;
  tuck = 0;
  reach = 0;
  pull = 0;
  phi = 0;
  phiDot = 0;
  theta = 0;
  omega = 0;
  L = 0;
  handX = 0;
  handY = 0;
  shoulderX = 0;
  shoulderY = 0;
  reachDirX = 0;
  reachDirY = 1;
  reachRadius = 0;
  armsOut = 0;
  assistBar = -1;
  barId = -1;
  lastBarId = -1;
  /** 1 while the hands are planted on a box. */
  planted = 0;
  /** The ledge the hands hold (-1 none), and 1 while the plant is a mantle over one. */
  ledgeSolid = -1;
  mantling = 0;
  /** Attach ease progress while attached, 0 at the grip tick to 1 when the arms have settled. */
  attachProgress = 1;
  /** The hands carry a salmon ladder bar between rungs; contact stays pinned throughout the lift. */
  ladderClimbing = false;
  state: number = S.Idle;
  stateTicks = 0;
  grounded = 0;
  facing = 1;
  /** Render-side facing blend, -1..1. */
  facingBlend = 1;
  /** Landing absorb: vertical compression (m) and its velocity. */
  absorb = 0;
  absorbVel = 0;
  /** Lean from acceleration, radians (world space, positive toward +x), smoothed. */
  accelLean = 0;
  accelLeanVel = 0;
  /** Whole-body axis angle from vertical (radians, positive tilts the head toward +x): along the
   * pendulum on a bar, the torso orientation in the air, upright on the ground. Blended onto the
   * pendulum over the attach ease at a catch; off the bar a critically damped spring that tracks the
   * sim's attitude and its rate (the sim carries the rotation now), so the spring only smooths the
   * handoffs between the ground, the air, and the bar. */
  bodyAngle = 0;
  /** Angular velocity of the body axis, rad/s (the pendulum's while attached). */
  bodyAngVel = 0;
  /** Seconds spent attached to the current bar (render clock), for the pin onto the pendulum. */
  private attachedSeconds = 0;
  /** Ground-only torso lean (tilt plus acceleration), radians, eased. Zero in the air and on a bar. */
  torsoLean = 0;
  height = 1.8;
  comY = 0.9;
  shoulderH = 1.45;
  ticksSinceLand = 1e9;
  ticksSinceRelease = 1e9;
  lastPumpGrade = 0;
  lastPumpTick = -1e9;
  tick = 0;
  /** Fractional tick of this view (tick + alpha). */
  time = 0;
  groundDistance = 0;
  /** True when this frame snapped (respawn), so followers should snap too. */
  snapped = false;

  /** Blend prev and next by alpha in [0, 1], then advance render-side state by dt seconds. */
  update(
    prev: SimState,
    next: SimState,
    alpha: number,
    t: CompiledTuning,
    feel: RenderFeel,
    dt: number,
  ): void {
    const a = alpha;
    // Rendering may skip the landing's exact simulation tick. The reset counter detects it once,
    // including hit-stop frames where tick advances but ticksSinceLand remains unchanged.
    const landedSinceRender = next.ticksSinceLand < this.ticksSinceLand;
    // Sim.reset() replaces both buffers, so their delta alone cannot reveal a restart. Nearby
    // checkpoint respawns and input restarts also need a snap instead of blending through terrain.
    const teleport =
      next.tick < this.tick ||
      ((prev.state === S.Fail || this.state === S.Fail) && next.state !== S.Fail) ||
      (next.prevRestart === 1 && prev.prevRestart === 0) ||
      Math.abs(next.x - prev.x) > 2 ||
      Math.abs(next.y - prev.y) > 2;
    const b = teleport ? 1 : a;
    this.snapped = teleport;
    this.x = prev.x + (next.x - prev.x) * b;
    this.y = prev.y + (next.y - prev.y) * b;
    this.vx = prev.vx + (next.vx - prev.vx) * b;
    this.vy = prev.vy + (next.vy - prev.vy) * b;
    this.ax = next.ax;
    this.tuck = prev.tuck + (next.tuck - prev.tuck) * b;
    this.reach = prev.reach + (next.reach - prev.reach) * b;
    this.pull = prev.pull + (next.pull - prev.pull) * b;
    // The attitude wraps at +-pi in a full turn; blend it the short way like the swing angle.
    this.phi = prev.phi + shortWay(prev.phi, next.phi) * b;
    this.phiDot = prev.phiDot + (next.phiDot - prev.phiDot) * b;
    this.omega = prev.omega + (next.omega - prev.omega) * b;
    this.L = prev.L + (next.L - prev.L) * b;
    // The swing angle wraps at the top of a giant; blend it the short way round so the frame
    // between the two ticks does not sweep the body through the bottom.
    this.theta = prev.theta + shortWay(prev.theta, next.theta) * b;
    this.handX = prev.handX + (next.handX - prev.handX) * b;
    this.handY = prev.handY + (next.handY - prev.handY) * b;
    this.shoulderX = prev.shoulderX + (next.shoulderX - prev.shoulderX) * b;
    this.shoulderY = prev.shoulderY + (next.shoulderY - prev.shoulderY) * b;
    this.reachDirX = prev.reachDirX + (next.reachDirX - prev.reachDirX) * b;
    this.reachDirY = prev.reachDirY + (next.reachDirY - prev.reachDirY) * b;
    this.reachRadius = prev.reachRadius + (next.reachRadius - prev.reachRadius) * b;
    this.armsOut = next.armsOut;
    this.assistBar = next.assistBar;
    this.barId = next.barId;
    this.planted = next.planted;
    this.ledgeSolid = next.ledgeSolid;
    this.mantling = next.mantling;
    this.lastBarId = next.lastBarId;
    this.attachProgress = next.attachTotal > 0 ? 1 - next.attachLeft / next.attachTotal : 1;
    // Include the seating interpolation frame: the lift ends in `next`, but alpha may still
    // show the bar just below its new pegs. Ordinary bars and legacy ladder replays keep their ease.
    this.ladderClimbing =
      t.ladderPerfectDuck >= 1 &&
      next.barId >= 0 &&
      (next.popping === 1 || (prev.popping === 1 && prev.ladderId === next.ladderId));
    this.state = next.state;
    this.stateTicks = next.stateTicks;
    this.grounded = next.grounded;
    this.facing = next.facing;
    this.ticksSinceLand = next.ticksSinceLand;
    this.ticksSinceRelease = next.ticksSinceRelease;
    this.lastPumpGrade = next.lastPumpGrade;
    this.lastPumpTick = next.lastPumpTick;
    this.tick = next.tick;
    this.time = next.tick - 1 + a;
    this.groundDistance = prev.groundDistance + (next.groundDistance - prev.groundDistance) * b;
    this.height = capsuleHeight(t, this.tuck);
    this.comY = comHeight(t, this.tuck);
    // A body turning over on the ground (a roll) is drawn about a mass lifted by up to rollLift when
    // upside down, so the curled silhouette rolls over the ground instead of through it.
    if (next.grounded === 1 && next.barId < 0) this.comY += feel.rollLift * 0.5 * (1 - Math.cos(this.phi));
    this.shoulderH = shoulderHeight(t, this.tuck);

    // Facing blend: eases toward the sim's facing over facingBlendMs.
    if (teleport) this.facingBlend = this.facing;
    else {
      const k = feel.facingBlendMs > 0 ? Math.min(1, (dt * 1000) / feel.facingBlendMs) : 1;
      this.facingBlend += (this.facing - this.facingBlend) * k;
    }

    // Landing absorb: a critically damped spring kicked by the impact speed.
    if (teleport) {
      this.absorb = 0;
      this.absorbVel = 0;
    }
    if (landedSinceRender) {
      this.absorbVel +=
        Math.min(feel.landingMax * 4, next.impactSpeed * feel.landingPerSpeed) *
        Math.sqrt(feel.landingStiffness);
    }
    const w = Math.sqrt(feel.landingStiffness);
    // Keep the normal 60 Hz response, but do not invert/explode the spring on a slow mobile frame.
    const landingSteps = Math.max(1, Math.ceil(dt * 60));
    const landingDt = dt / landingSteps;
    for (let i = 0; i < landingSteps; i++) {
      const accel = -feel.landingStiffness * this.absorb - 2 * feel.landingDamping * w * this.absorbVel;
      this.absorbVel += accel * landingDt;
      this.absorb += this.absorbVel * landingDt;
      if (this.absorb > feel.landingMax) {
        this.absorb = feel.landingMax;
        if (this.absorbVel > 0) this.absorbVel = 0;
      }
      if (this.absorb < -feel.landingMax * 0.5) this.absorb = -feel.landingMax * 0.5;
    }

    // Lean from acceleration through an underdamped spring: a hard stop whips the torso forward
    // before it settles back, and a start snaps into the lean with a little overshoot, so the body
    // reads as a mass with momentum.
    let target = this.ax * feel.leanPerAccel;
    if (target > feel.leanMax) target = feel.leanMax;
    if (target < -feel.leanMax) target = -feel.leanMax;
    if (next.barId >= 0 || next.grounded === 0) target = 0;
    if (teleport) {
      this.accelLean = target;
      this.accelLeanVel = 0;
    } else {
      const wl = 2 * Math.PI * feel.leanHz;
      const sub = dt > 0.012 ? Math.ceil(dt / 0.008) : 1;
      const h = dt / sub;
      for (let k = 0; k < sub; k++) {
        const la = -wl * wl * (this.accelLean - target) - 2 * feel.leanDamping * wl * this.accelLeanVel;
        this.accelLeanVel += la * h;
        this.accelLean += this.accelLeanVel * h;
      }
    }

    // Whole-body axis and ground torso lean, both continuous so a catch, a release, or a landing
    // never pops.
    // A roll turns the whole body with the sim's attitude, so it follows phi like a flight.
    const onGround = next.grounded === 1 && next.barId < 0 && next.state !== S.Roll;
    const attached = next.barId >= 0;
    // Targets are unwrapped against the current body angle, the short way round: the pendulum
    // angle and the attitude both wrap at +-pi, and the drawn axis must follow the short way.
    let bodyTarget = attached ? -this.theta : onGround ? 0 : this.phi;
    const bodyTargetVel = attached ? -this.omega : onGround ? 0 : this.phiDot;
    if (!teleport) bodyTarget = this.bodyAngle + shortWay(this.bodyAngle, bodyTarget);
    const leanTarget = onGround ? this.phi + this.accelLean : 0;
    if (!attached) this.attachedSeconds = 0;
    if (teleport) {
      this.bodyAngle = bodyTarget;
      this.bodyAngVel = 0;
      this.torsoLean = leanTarget;
      if (attached) this.attachedSeconds = 1;
    } else {
      const k = halfLifeFactor(feel.bodyAngleHalfLife, dt);
      this.torsoLean += (leanTarget - this.torsoLean) * k;
      if (attached) {
        // Onto the pendulum: eased with the half-life, and pinned exactly to it over the first
        // fifth of a second so the arms stay straight once the swing is under way; carry its
        // rotation rate so a release starts from it.
        this.attachedSeconds += dt;
        const pin = Math.min(1, this.attachedSeconds / ATTACH_PIN_SECONDS);
        const eased = this.bodyAngle + (bodyTarget - this.bodyAngle) * k;
        this.bodyAngle = eased + (bodyTarget - eased) * pin * pin;
        this.bodyAngVel = -this.omega;
      } else {
        // Critically damped spring toward upright (ground) or the sim's attitude (air), with the
        // target's rate fed forward so a steady spin is tracked without lag.
        const wn = 2 * Math.PI * feel.bodyAngleHz;
        const sub = dt > 0.012 ? Math.ceil(dt / 0.008) : 1;
        const h = dt / sub;
        let x = this.bodyAngle;
        let v = this.bodyAngVel;
        for (let i = 0; i < sub; i++) {
          v += (-wn * wn * (x - bodyTarget) - 2 * wn * (v - bodyTargetVel)) * h;
          x += v * h;
        }
        this.bodyAngle = x;
        this.bodyAngVel = v;
      }
    }
  }
}
